import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, MicOff, Save, ArrowLeft, Activity } from 'lucide-react'
import useAudioRecorder from '../hooks/useAudioRecorder'
import useWebSocket from '../hooks/useWebSocket'

const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/nim`

interface PresenterViewProps {
  token: string
  onBack: () => void
  captureType?: 'microphone' | 'browser'
  sourceLang?: string
  targetLang?: string
}

interface TranscriptSegment {
  id: number
  sourceText: string
  translatedText: string
  timestamp: number
}

const PresenterView: React.FC<PresenterViewProps> = ({ token, onBack, captureType = 'microphone', sourceLang = 'vi', targetLang = 'en' }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [segments, setSegments] = useState<TranscriptSegment[]>([])
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected')
  const [isSaving, setIsSaving] = useState(false)
  const segmentIdRef = useRef(0)

  const handleWebSocketMessage = useCallback((data: any) => {
    if (data.type === 'transcript') {
      const newSegment: TranscriptSegment = {
        id: segmentIdRef.current++,
        sourceText: data.source_text || '',
        translatedText: data.translated_text || '',
        timestamp: Date.now(),
      }
      
      setSegments(prev => [...prev, newSegment])
    } else if (data.type === 'status') {
      console.log('Status:', data.message)
    } else if (data.type === 'error') {
      console.error('Error:', data.message)
    }
  }, [])

  const { sendMessage, sendBytes, status: wsStatus } = useWebSocket({
    url: `${WS_URL}/ws/presenter?token=${token}`,
    onMessage: handleWebSocketMessage,
  })

  useEffect(() => {
    setConnectionStatus(wsStatus)
  }, [wsStatus])

  const handleAudioData = useCallback((audioChunk: ArrayBuffer) => {
    if (isRecording) {
      sendBytes(new Uint8Array(audioChunk))
    }
  }, [isRecording, sendBytes])

  const { startRecording, stopRecording } = useAudioRecorder({
    onAudioData: handleAudioData,
    chunkDurationMs: 300,
    captureType,
  })

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording()
      setIsRecording(false)
      // Send stop signal
      sendMessage(JSON.stringify({ action: 'stop' }))
    } else {
      startRecording()
      setIsRecording(true)
      // Send start signal with language pair
      sendMessage(JSON.stringify({ 
        action: 'start',
        source_language: sourceLang,
        target_language: targetLang
      }))
    }
  }, [isRecording, startRecording, stopRecording, sendMessage, sourceLang, targetLang])

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      sendMessage(JSON.stringify({ action: 'stop_recording' }))
    } finally {
      setIsSaving(false)
    }
  }, [sendMessage])

  return (
    <div className="presenter-view">
      <div className="presenter-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="status-indicator">
          <Activity size={16} />
          <span className={`status-${connectionStatus}`}>
            {connectionStatus}
          </span>
        </div>
      </div>

      <div className="presenter-content">
        <div className="transcript-panel">
          <h3>Translation</h3>
          <div className="segments-list">
            {segments.length === 0 && (
              <p className="empty-text">Press the microphone button to start speaking {sourceLang === 'vi' ? 'Vietnamese' : 'English'}...</p>
            )}
            {segments.map((segment) => (
              <div key={segment.id} className="segment fade-in">
                <div className="source-text">{segment.sourceText}</div>
                <div className="translated-text">{segment.translatedText}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="presenter-controls">
        <button
          className={`record-btn ${isRecording ? 'recording' : ''}`}
          onClick={toggleRecording}
        >
          {isRecording ? (
            <>
              <div className="pulse-ring" />
              <MicOff size={32} />
            </>
          ) : (
            <Mic size={32} />
          )}
        </button>

        <button
          className="save-btn"
          onClick={handleSave}
          disabled={isSaving || segments.length === 0}
        >
          <Save size={20} />
          <span>{isSaving ? 'Saving...' : 'Save'}</span>
        </button>
      </div>
    </div>
  )
}

export default PresenterView
